import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { EntityForm } from '../components/common/EntityForm';
import { useEntity } from '../hooks/useEntity';
import { useEntityMutation } from '../hooks/useEntityMutation';
import { useToast } from '../components/common/Toast';
import { getEntityConfig } from '../lib/entity-config';

export function EntityEditPage() {
  const { entityName, id } = useParams<{ entityName: string; id: string }>();
  const navigate = useNavigate();
  const config = getEntityConfig(entityName ?? '');
  const { record, isLoading } = useEntity(entityName ?? '', id ?? '');
  const { updateEntity, isSubmitting } = useEntityMutation();
  const { showToast } = useToast();

  if (!config) return <div className="text-red-500">Unknown entity: {entityName}</div>;
  if (isLoading) return <div className="text-slate-500 py-8 text-center">Loading...</div>;
  if (!record) return <div className="text-slate-500 py-8 text-center">Record not found</div>;

  // Extract initial values from record attributes
  const initialValues: Record<string, any> = {};
  for (const attr of record.Attributes) {
    // Preserve full FK EntityReference {Id, Name, LogicalName} for the FkSelect dropdown
    if (typeof attr.Value === 'object' && attr.Value !== null && 'Id' in attr.Value) {
      initialValues[attr.Key] = attr.Value;
    } else if (typeof attr.Value === 'object' && attr.Value !== null && 'Value' in attr.Value) {
      initialValues[attr.Key] = attr.Value; // preserve {Value: number} for OptionSet fields
    } else {
      initialValues[attr.Key] = attr.Value;
    }
  }

  async function handleSubmit(values: Record<string, any>) {
    try {
      await updateEntity(entityName!, { [config!.primaryKeyField]: id, ...values });
      showToast(`${config!.displayName} updated successfully`);
      navigate(`/${entityName}/${id}`);
    } catch (err: any) {
      showToast(err.message, 'error');
    }
  }

  return (
    <EntityForm
      entityName={entityName!}
      displayName={config.displayName}
      fields={config.formFields}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      isEdit
      isSubmitting={isSubmitting}
    />
  );
}
